import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MultiSelectTicketRow } from '../detaille-multi-selection-ticket/detaille-multi-selection-ticket.component';

/** Mode de génération de la facture depuis les tickets. */
export type TicketBatchFactureMode = 'financial' | 'product_lines';

/** Produit financier proposé dans le modal (Recette, etc.). */
export interface ModalFinancialProduct {
  id: number;
  nom?: string;
  ref_interne?: string;
  tva_id?: number | null;
}

export interface TicketBatchProceedPayload {
  ticketIds: number[];
  tickets: {
    id: number;
    numero: string;
    date: string;
    total: number;
    subtotal?: number;
    sousType?: string | null;
    entrepotId: number | null;
    entrepotCode: string;
    entrepot: string;
    clientId?: number | null;
    clientType?: 'particulier' | 'patente';
    reglement?: string | null;
    paymentModes?: { libelle: string; montant: number }[];
  }[];
  /** facture uniquement : produits financiers vs lignes produits. */
  mode?: TicketBatchFactureMode;
  /** facture + mode financier : produit financier choisi (Recette par défaut). */
  financialProductId?: number | null;
}

@Component({
  selector: 'app-facturer-multi-ticket-modal',
  templateUrl: './facturer-multi-ticket-modal.component.html',
  styleUrls: [
    './facturer-multi-ticket-modal.component.scss',
    '../annuler-multi-ticket-modal/annuler-multi-ticket-modal.component.scss',
  ],
})
export class FacturerMultiTicketModalComponent implements OnChanges {
  @Input() ticketIds: number[] = [];
  @Input() submitting = false;
  @Input() allowConsolideTickets = false;
  /** facture uniquement : produits financiers proposés (Recette + autres). */
  @Input() financialProducts: ModalFinancialProduct[] = [];

  @Output() closed = new EventEmitter<void>();
  @Output() proceedToDocument = new EventEmitter<TicketBatchProceedPayload>();

  rows: MultiSelectTicketRow[] = [];
  canProceed = false;
  blockReason: string | null = null;
  excludedCount = 0;

  /** 1 = liste tickets, 2 = choix (financier / lignes), 3 = choix produit financier. */
  step: 1 | 2 | 3 = 1;
  factureMode: TicketBatchFactureMode = 'financial';
  selectedFinancialProductId: number | null = null;

  get modalTitle(): string {
    return 'Génération de facture';
  }

  get introHtml(): string {
    return (
      'Seuls les tickets facturables et compatibles entre eux restent (même entrepôt, même client). ' +
      'Les autres sont retirés automatiquement (clôture, déjà facturés, entrepôt ou client différent, etc.). ' +
      '<strong>Suivant</strong> permet de choisir le contenu de la facture.'
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ticketIds'] && !changes['ticketIds'].firstChange) {
      this.rows = [];
      this.canProceed = false;
      this.step = 1;
      this.factureMode = 'financial';
      this.selectedFinancialProductId = null;
    }
  }

  productLabel(p: ModalFinancialProduct): string {
    const name = (p.nom || '').trim() || 'Produit financier';
    const ref = (p.ref_interne || '').trim();
    return ref ? `${name} (${ref})` : name;
  }

  onRowsChange(rows: MultiSelectTicketRow[]): void {
    this.rows = rows;
  }

  onCanProceedChange(can: boolean): void {
    this.canProceed = can;
  }

  onBlockReasonChange(reason: string | null): void {
    this.blockReason = reason;
  }

  onExcludedCountChange(count: number): void {
    this.excludedCount = count;
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  onPanelClick(event: Event): void {
    event.stopPropagation();
  }

  onClose(): void {
    if (this.submitting) {
      return;
    }
    this.closed.emit();
  }

  private buildPayload(
    mode?: TicketBatchFactureMode,
    financialProductId?: number | null
  ): TicketBatchProceedPayload {
    return {
      ticketIds: this.rows.map((r) => r.id),
      tickets: this.rows.map((r) => ({
        id: r.id,
        numero: r.numero,
        date: r.date,
        total: r.total,
        subtotal: r.subtotal,
        sousType: r.sousType,
        entrepotId: r.entrepotId,
        entrepotCode: r.entrepotCode,
        entrepot: r.entrepot,
        clientId: r.clientId,
        clientType: r.clientType,
        reglement: r.reglement,
        paymentModes: r.paymentModes,
      })),
      ...(mode ? { mode } : {}),
      ...(financialProductId != null ? { financialProductId } : {}),
    };
  }

  onSuivant(): void {
    if (!this.canProceed || this.submitting || this.rows.length === 0) {
      return;
    }
    this.step = 2;
  }

  backToList(): void {
    if (this.submitting) {
      return;
    }
    this.step = 1;
  }

  selectFactureMode(mode: TicketBatchFactureMode): void {
    this.factureMode = mode;
  }

  onSuivantFromChoice(): void {
    if (this.submitting || this.rows.length === 0) {
      return;
    }
    if (this.factureMode === 'product_lines') {
      this.proceedToDocument.emit(this.buildPayload('product_lines'));
      return;
    }
    if (this.selectedFinancialProductId == null && this.financialProducts.length) {
      this.selectedFinancialProductId = this.financialProducts[0].id;
    }
    this.step = 3;
  }

  backToChoice(): void {
    if (this.submitting) {
      return;
    }
    this.step = 2;
  }

  selectFinancialProduct(id: number): void {
    this.selectedFinancialProductId = id;
  }

  confirmFinancial(): void {
    if (this.submitting || this.rows.length === 0) {
      return;
    }
    this.proceedToDocument.emit(
      this.buildPayload('financial', this.selectedFinancialProductId)
    );
  }
}
